import type { Pillar, TrackerKpi, TrackerTarget, Week } from 'chery-contracts';
import type { RangeSummary } from 'chery-kpi-engine';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { PILLAR_CLASSES, PILLAR_META } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { indentRem, unitShortLabel } from '../kpis/kpi-display';
import { GridCell, type GridCellHandlers } from './GridCell';
import {
  calculatedCellTitle,
  cellText,
  cellTitle,
  childRowName,
  childrenSumHint,
  editorText,
  kpiTitle,
  movementDisplay,
  summaryTexts,
  targetDisplay,
  weekColumnLabel,
} from './tracker-display';
import type { CellValue } from './tracker-state';
import type { TrackerEditing } from './use-tracker-editing';

export interface PillarGroup {
  pillar: Pillar;
  access: 'VIEW' | 'EDIT';
  kpis: TrackerKpi[];
  collapsed: boolean;
}

export interface TrackerGridProps {
  groups: readonly PillarGroup[];
  weeks: readonly Week[];
  previousWeek: Week | null;
  currentWeekId: string | null;
  editing: TrackerEditing;
  /** Every cell's value as shown: a manual cell with its staged edit, a calculated one through the engine's resolver. */
  valueAt: (kpiId: string, weekId: string) => CellValue;
  summaryOf: (kpiId: string) => RangeSummary;
  /** Active targets per KPI and the range's To week, which picks the one shown. */
  targets: Readonly<Record<string, readonly TrackerTarget[]>>;
  /** Child KPIs by parent id over the whole grid (not only the filtered rows), for the children-vs-parent marker. */
  childrenOf: ReadonlyMap<string, readonly TrackerKpi[]>;
  /** KPI names by id, so a child row can drop its parent's name. */
  nameOf: (kpiId: string) => string | null;
  toWeekDate: string | null;
  showComments: boolean;
  onToggleCollapse: (pillar: Pillar) => void;
  onOpenComment: (kpiId: string, weekId: string) => void;
  empty: ReactNode;
}

const STICKY_HEAD = 'sticky top-0 z-20 border-b border-border bg-muted';

/**
 * The weekly grid (plan §2.2, §7): sticky KPI and unit columns, a sticky week header with the current week
 * highlighted, pillar bands in the pillar tint, monospace right-aligned numbers, and the Total / Average / Latest
 * columns of the selected range computed in the browser over the staged edits, then the Target column with its
 * completion line. Calculated cells are read-only, marked `ƒ`, and recalculate while a source is being typed.
 */
export const TrackerGrid = ({
  groups,
  weeks,
  previousWeek,
  currentWeekId,
  editing,
  valueAt,
  summaryOf,
  targets,
  childrenOf,
  nameOf,
  toWeekDate,
  showComments,
  onToggleCollapse,
  onOpenComment,
  empty,
}: TrackerGridProps) => {
  const { t } = useI18nTranslation();
  const { recordAt, editAt, isSelected, focus, editing: editingCell } = editing;

  const handlers: GridCellHandlers = useMemo(
    () => ({
      onMouseDown: (kpiId, weekId, extend) => editing.beginDrag({ kpiId, weekId }, extend),
      onMouseEnter: (kpiId, weekId) => editing.dragOver({ kpiId, weekId }),
      onDoubleClick: (kpiId, weekId) => editing.startEdit({ kpiId, weekId }),
      onOpenComment,
      onCommit: editing.commitEdit,
      onCancel: editing.cancelEdit,
      onDraft: editing.onDraft,
      onPasteBlock: (text, kpiId, weekId) => editing.pasteBlock(text, { kpiId, weekId }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the callbacks are stable; listing `editing` would rebuild this every render.
    [
      editing.beginDrag,
      editing.dragOver,
      editing.startEdit,
      editing.commitEdit,
      editing.cancelEdit,
      editing.onDraft,
      editing.pasteBlock,
      onOpenComment,
    ],
  );

  const columnCount = weeks.length + 6;
  const hasRows = groups.some((group) => group.kpis.length > 0);

  return (
    <table
      role="grid"
      aria-label={t('tracker.gridLabel')}
      className="border-separate border-spacing-0 text-sm"
    >
      <thead>
        <tr>
          <th
            scope="col"
            className={cn(
              STICKY_HEAD,
              'start-0 z-30 w-72 min-w-[18rem] border-e px-3 py-2 text-start text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
            )}
          >
            {t('tracker.columns.kpi')}
          </th>
          <th
            scope="col"
            className={cn(
              STICKY_HEAD,
              'start-72 z-30 w-16 min-w-[4rem] border-e-2 px-2 py-2 text-start text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
            )}
          >
            {t('tracker.columns.unit')}
          </th>
          {weeks.map((week) => {
            const label = weekColumnLabel(week);
            const isCurrent = week.id === currentWeekId;

            return (
              <th
                key={week.id}
                scope="col"
                data-week-id={week.id}
                data-current-week={isCurrent || undefined}
                title={isCurrent ? `${week.label} · ${t('tracker.currentWeek')}` : week.label}
                className={cn(STICKY_HEAD, 'border-e px-2 py-1.5 text-end font-normal', isCurrent && 'bg-primary/15 text-primary')}
              >
                <span className="block font-mono text-xs font-semibold">{label.date}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {isCurrent ? `${t('tracker.currentWeekShort')} · ` : ''}
                  {label.number}
                </span>
              </th>
            );
          })}
          {(['total', 'average', 'latest', 'target'] as const).map((column, index) => (
            <th
              key={column}
              scope="col"
              className={cn(
                STICKY_HEAD,
                'min-w-[8.5rem] border-e px-2 py-2 text-end text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
                index === 0 && 'border-s-2',
              )}
            >
              {t(`tracker.columns.${column}`)}
            </th>
          ))}
        </tr>
      </thead>

      {hasRows ? (
        groups.map((group) => {
          const classes = PILLAR_CLASSES[group.pillar];

          return (
            <tbody
              key={group.pillar}
              data-pillar={group.pillar}
            >
              <tr className={classes.band}>
                <th
                  scope="rowgroup"
                  colSpan={columnCount}
                  className={cn('h-8 border-b px-0 text-start', classes.border)}
                >
                  <div className="sticky start-0 inline-flex items-center gap-2 px-3">
                    <button
                      type="button"
                      onClick={() => onToggleCollapse(group.pillar)}
                      aria-expanded={!group.collapsed}
                      aria-label={t(group.collapsed ? 'tracker.expand' : 'tracker.collapse', { pillar: PILLAR_META[group.pillar].label })}
                      className={cn('rounded p-0.5 hover:bg-background/40', classes.text)}
                    >
                      {group.collapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
                    </button>
                    <span className={cn('size-2 rounded-sm', classes.dot)} />
                    <span className={cn('text-sm font-semibold', classes.text)}>{PILLAR_META[group.pillar].label}</span>
                    <span className={cn('text-xs opacity-75', classes.text)}>
                      {t('kpis.count', { count: group.kpis.length })}
                      {group.access === 'VIEW' ? ` · ${t('tracker.viewOnly')}` : ''}
                    </span>
                  </div>
                </th>
              </tr>

              {group.collapsed
                ? null
                : group.kpis.map((kpi) => {
                    const rangeSummary = summaryOf(kpi.id);
                    const summary = summaryTexts(kpi, rangeSummary);
                    const target = targetDisplay(kpi, targets[kpi.id] ?? [], toWeekDate, rangeSummary);
                    const calculated = kpi.source === 'CALCULATED';
                    const children = childrenOf.get(kpi.id);

                    return (
                      <tr
                        key={kpi.id}
                        data-kpi-id={kpi.id}
                        data-kpi-code={kpi.code}
                        data-editable={kpi.editable}
                        className="bg-card"
                      >
                        <th
                          scope="row"
                          title={kpiTitle(kpi)}
                          className="sticky start-0 z-10 h-8 w-72 min-w-[18rem] max-w-[18rem] border-b border-e border-border/60 bg-card px-3 text-start font-normal"
                        >
                          <div
                            className="flex items-center gap-2"
                            style={{ paddingInlineStart: `${indentRem(kpi.depth)}rem` }}
                          >
                            <span className={cn('h-3.5 w-1 shrink-0 rounded-sm', PILLAR_CLASSES[kpi.pillar].dot)} />
                            {kpi.depth > 0 ? <span className="text-muted-foreground">↳</span> : null}
                            <span className="truncate text-[13px] text-foreground">
                              {kpi.depth > 0 && kpi.parentKpiId ? childRowName(kpi.name, nameOf(kpi.parentKpiId)) : kpi.name}
                            </span>
                            {calculated ? (
                              <span
                                className="shrink-0 font-serif text-xs italic text-violet-700/80 dark:text-violet-300/80"
                                title={kpi.restricted ? t('tracker.restrictedHint') : `ƒ = ${kpi.formulaPreview ?? ''}`}
                              >
                                ƒ
                              </span>
                            ) : null}
                            {kpi.restricted ? <span className="shrink-0 text-[10px] text-muted-foreground">{t('tracker.restricted')}</span> : null}
                            <span
                              className="ms-auto shrink-0 text-[10px] text-muted-foreground"
                              title={t(`enums.valueTypeHint.${kpi.valueType}`)}
                            >
                              {t(`enums.valueType.${kpi.valueType}`)}
                            </span>
                          </div>
                        </th>
                        <td className="sticky start-72 z-10 h-8 w-16 min-w-[4rem] border-b border-e-2 border-border/60 bg-card px-2 font-mono text-xs text-muted-foreground">
                          {unitShortLabel(kpi.unit, kpi.currency)}
                        </td>

                        {weeks.map((week, col) => {
                          const record = recordAt(kpi.id, week.id);
                          const value = valueAt(kpi.id, week.id);
                          const edit = calculated ? undefined : editAt(kpi.id, week.id);
                          const previousRef = col > 0 ? weeks[col - 1] : previousWeek;
                          const movement =
                            edit?.invalid === undefined && previousRef
                              ? movementDisplay(
                                  kpi,
                                  { label: weekColumnLabel(week).date, value },
                                  { label: weekColumnLabel(previousRef).date, value: valueAt(kpi.id, previousRef.id) },
                                )
                              : null;
                          const sumHint = children
                            ? childrenSumHint(
                                kpi,
                                value,
                                children.map((child) => ({ unit: child.unit, currency: child.currency, value: valueAt(child.id, week.id) })),
                              )
                            : null;
                          const isEditing = editingCell !== null && editingCell.kpiId === kpi.id && editingCell.weekId === week.id;

                          return (
                            <GridCell
                              key={week.id}
                              kpiId={kpi.id}
                              weekId={week.id}
                              text={edit?.invalid ?? cellText(kpi, value)}
                              glyph={movement?.glyph ?? null}
                              glyphTitle={movement?.title ?? null}
                              title={calculated ? calculatedCellTitle(kpi, week.weekDate) : cellTitle(kpi.name, week.weekDate, record)}
                              isBlank={value === null && edit?.invalid === undefined}
                              editable={kpi.editable}
                              calculated={calculated}
                              selected={isSelected(kpi.id, week.id)}
                              focused={focus !== null && focus.kpiId === kpi.id && focus.weekId === week.id}
                              edited={edit !== undefined}
                              invalid={edit?.invalid !== undefined}
                              hasComment={!calculated && showComments && Boolean(record.comment || record.reason || record.followUp)}
                              isCurrentWeek={week.id === currentWeekId}
                              childrenHint={sumHint ? t('tracker.childrenMismatch', { ...sumHint }) : null}
                              editorValue={isEditing ? (editingCell.seed ?? editorText(record.value, edit?.invalid)) : null}
                              handlers={handlers}
                            />
                          );
                        })}

                        {(['total', 'average', 'latest'] as const).map((column, index) => (
                          <td
                            key={column}
                            data-summary={column}
                            className={cn(
                              'h-8 border-b border-e border-border/60 bg-muted/40 px-2 text-end font-mono text-xs',
                              index === 0 && 'border-s-2 font-semibold',
                              summary[column] === '—' ? 'text-muted-foreground/60' : 'text-foreground',
                            )}
                          >
                            {summary[column]}
                          </td>
                        ))}

                        <td
                          data-summary="target"
                          title={target?.title}
                          className="h-8 min-w-[8.5rem] border-b border-e border-border/60 bg-muted/40 px-2 text-end align-middle font-mono text-xs"
                        >
                          {target ? (
                            <span className="flex flex-col items-end leading-tight">
                              <span className="text-foreground">{target.value}</span>
                              <span
                                data-completion
                                className="text-[10px] text-muted-foreground"
                              >
                                {target.completion} · {target.direction}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          );
        })
      ) : (
        <tbody>
          <tr>
            <td
              colSpan={columnCount}
              className="px-6 py-16 text-center text-sm text-muted-foreground"
            >
              {empty}
            </td>
          </tr>
        </tbody>
      )}
    </table>
  );
};
